/**
 * cyfunReportData.ts - construit les données du Rapport CyFun à partir des
 * résultats de scan (M365 + Azure). Réutilisable hors de la page CyFun
 * (ex: page Rapports). Aligné sur la logique de CyFunReportPage.
 */
import {
  CYFUN_REQUIREMENTS, levelsForTarget,
  type CyFunLevel, type CyFunControlType,
} from '../domain/cyfunFramework';
import { controls, controlsById, familyLabels } from '../domain/controls';
import { AZURE_CONTROLS } from '../domain/azureControls';
import { MANUAL_GOVERNANCE_CONTROLS } from '../domain/manualControls';
import type { CyFunReportRow, CyFunReportMeta, CyFunCompany, CyFunInventoryRow } from './generateCyFunReport';

const AZURE_TITLE_BY_ID: Record<string, string> = Object.fromEntries(AZURE_CONTROLS.map((c) => [c.id, c.title]));

export interface ScanHit { status: string; date?: string; hasEvidence: boolean; message?: string; }

interface RawResult {
  controlId?: string; ControlId?: string;
  status?: string; Status?: string;
  evaluatedAt?: string; EvaluatedAt?: string;
  message?: string; Message?: string;
  evidences?: unknown[]; Evidences?: unknown[];
  evidenceUrl?: string | null;
}

export function buildScanByControl(m365Results: readonly unknown[], azureResults: readonly unknown[]): Map<string, ScanHit> {
  const map = new Map<string, ScanHit>();
  for (const raw of m365Results ?? []) {
    const r = raw as RawResult;
    const cid = String(r.controlId ?? r.ControlId ?? '').toLowerCase();
    if (!cid) continue;
    const ev = (r.evidences ?? r.Evidences ?? []) as unknown[];
    map.set(cid, {
      status:      String(r.status ?? r.Status ?? '').toLowerCase(),
      date:        (r.evaluatedAt ?? r.EvaluatedAt) as string | undefined,
      hasEvidence: Array.isArray(ev) && ev.length > 0,
      message:     String(r.message ?? r.Message ?? '') || undefined,
    });
  }
  for (const raw of azureResults ?? []) {
    const r = raw as RawResult;
    const cid = String(r.controlId ?? '').toLowerCase();
    if (!cid) continue;
    map.set(cid, { status: String(r.status ?? '').toLowerCase(), date: r.evaluatedAt, hasEvidence: !!r.evidenceUrl, message: r.message });
  }
  return map;
}

type Conf = 'pass' | 'fail' | 'warning' | 'na' | 'manual' | 'external' | 'partial';

/**
 * Statistiques de conformité partagées (page + PDF/Word + Excel).
 *  A — le "Partiel" (et "À améliorer"/warning) est compté à part, plus dans les non conformes.
 *  B — taux de conformité pondéré : Conforme = 1, Partiel = 0,5, Non conforme = 0,
 *      sur le périmètre réellement évalué (hors Manuel / Externe / Non évalué).
 */
export interface ConfStats {
  total: number; conformes: number; partiels: number; nonConf: number;
  nonEval: number; keyMeasures: number; evaluated: number; score: number;
}
export function confStats(rows: ReadonlyArray<{ conf: string; keyMeasure?: boolean }>): ConfStats {
  const conformes = rows.filter((r) => r.conf === 'pass').length;
  const partiels  = rows.filter((r) => r.conf === 'partial' || r.conf === 'warning').length;
  const nonConf   = rows.filter((r) => r.conf === 'fail').length;
  const nonEval   = rows.filter((r) => r.conf === 'na' || r.conf === 'manual' || r.conf === 'external').length;
  const keyMeasures = rows.filter((r) => r.keyMeasure).length;
  const evaluated = conformes + partiels + nonConf;
  const score = evaluated > 0 ? Math.round(((conformes + 0.5 * partiels) / evaluated) * 100) : 0;
  return { total: rows.length, conformes, partiels, nonConf, nonEval, keyMeasures, evaluated, score };
}

function evaluate(scanByControl: Map<string, ScanHit>, m365: string[], azure: string[], type: CyFunControlType) {
  const ids = [...m365, ...azure].map((x) => x.toLowerCase());
  const hits = ids.map((id) => scanByControl.get(id)).filter(Boolean) as ScanHit[];
  let conf: Conf;
  if (type === 'external') {
    conf = 'external';
  } else {
    const scored = hits.filter((h) => ['pass', 'fail', 'error', 'warning', 'warn'].includes(h.status));
    if (scored.length === 0) {
      conf = type === 'manual' ? 'manual' : 'na';
    } else {
      const pass = scored.filter((h) => h.status === 'pass').length;
      const fail = scored.filter((h) => h.status === 'fail' || h.status === 'error').length;
      if (pass === scored.length)  conf = 'pass';
      else if (pass === 0)         conf = fail > 0 ? 'fail' : 'warning';
      else                         conf = 'partial';
    }
  }
  const hasEvidence = hits.some((h) => h.hasEvidence);
  const lastDate = hits.map((h) => h.date).filter(Boolean).sort().reverse()[0];
  return { conf, hasEvidence, lastDate };
}

// ============================================================================
//  buildCyFunReport - exporte CyFunReportRow[] pour PDF/Word/page
// ============================================================================
export function buildCyFunReport(
  scanByControl: Map<string, ScanHit>,
  target: CyFunLevel,
  opts: { orgName: string; company?: CyFunCompany },
): { rr: CyFunReportRow[]; meta: CyFunReportMeta } {
  const allowed = levelsForTarget(target);
  const mk = (id: string, kind: 'm365' | 'azure') => {
    const hit = scanByControl.get(id.toLowerCase());
    return {
      id, kind,
      title:  kind === 'azure' ? (AZURE_TITLE_BY_ID[id] ?? '') : (controlsById[id]?.title ?? ''),
      status: hit?.status ?? '',
      message: hit?.message,
      date:    hit?.date,
      hasEvidence: hit?.hasEvidence ?? false,
    };
  };
  const rr: CyFunReportRow[] = CYFUN_REQUIREMENTS
    .filter((r) => r.id && allowed.includes(r.level))
    .map((r) => {
      const e = evaluate(scanByControl, r.m365 ?? [], r.azure ?? [], r.type as CyFunControlType);
      return {
        id: r.id, level: r.level, function: r.function, category: r.category,
        requirement: r.requirement, keyMeasure: !!r.keyMeasure, type: r.type as string,
        conf: e.conf, hasEvidence: e.hasEvidence, lastDate: e.lastDate,
        controls: [...(r.m365 ?? []), ...(r.azure ?? []), ...(r.manual ?? [])],
        controlResults: [
          ...(r.m365 ?? []).map((id) => mk(id, 'm365')),
          ...(r.azure ?? []).map((id) => mk(id, 'azure')),
        ],
      };
    });
  const meta: CyFunReportMeta = {
    orgName: opts.orgName,
    targetLevel: target,
    date: new Date().toISOString(),
    referential: 'CCB CyberFundamentals (CyFun) v2024-01-08',
    company: opts.company,
  };
  return { rr, meta };
}

// ============================================================================
//  buildControlInventory - matrice contrôles M365 + Azure + Manuels
// ============================================================================
/** Statut FR d'un contrôle à partir de son statut de scan brut. */
function frControlStatus(s?: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'pass') return 'Conforme';
  if (v === 'fail' || v === 'error') return 'Non conforme';
  if (v === 'warning' || v === 'warn') return 'À améliorer';
  if (v === 'partial') return 'Partiel';
  if (v === 'manual') return 'Manuel';
  return 'Non évalué';
}

export function buildControlInventory(scanByControl: Map<string, ScanHit>): CyFunInventoryRow[] {
  const st = (id: string) => frControlStatus(scanByControl.get(id.toLowerCase())?.status);
  return [
    ...controls.map((c) => ({ source: 'M365', category: familyLabels[c.family] ?? c.family, id: c.id, title: c.title, status: st(c.id) })),
    ...AZURE_CONTROLS.map((c) => ({ source: 'Azure', category: c.family, id: c.id, title: c.title, status: st(c.id) })),
    ...MANUAL_GOVERNANCE_CONTROLS.map((c) => ({ source: 'Manuel', category: c.family, id: c.id, title: c.title, status: scanByControl.has(c.id.toLowerCase()) ? st(c.id) : 'Manuel' })),
  ];
}
